import { Link, useNavigate } from 'react-router-dom';
import { paidPlanLabel } from '@/utils/entitlements';
import { format, startOfDay, eachDayOfInterval } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, StatCard, Table, Badge } from '@/components';
import { SignInAgain } from '@/components/SignInAgain';
import { AriaBalanceCard } from '@/components/AriaBalanceCard';
import { useSessions, useVehicles, useReports, useEntitlement } from '@/hooks/useProApi';
import { formatVehicle, formatVin, formatDuration, utcToLocal, type SessionSummary } from '@/types';

// Stat card icons (PNG)
import dashboardIcon from '@/icons/icons8-dashboard-64.png';
import carsIcon from '@/icons/icons8-cars-50.png';
import reportsIcon from '@/icons/icons8-reports-50.png';

export function Dashboard() {
  const navigate = useNavigate();
  const { data: entitlement, isLoading: entitlementLoading, error: entitlementError } = useEntitlement();
  const { data: sessionsData, isLoading: sessionsLoading } = useSessions({ pageSize: 200 });
  const { data: vehiclesData, isLoading: vehiclesLoading } = useVehicles();
  const { data: reportsData, isLoading: reportsLoading } = useReports();

  const isLoading = entitlementLoading || sessionsLoading || vehiclesLoading || reportsLoading;

  // Calculate stats
  const totalSessions = sessionsData?.totalCount || 0;
  const totalVehicles = vehiclesData?.vehicles?.length || 0;
  const totalReports = reportsData?.reports?.length || 0;

  // Prepare chart data: sessions per day
  const chartData = (() => {
    const sessions = sessionsData?.sessions;
    if (!sessions || sessions.length === 0) return [];

    // Count sessions per calendar day
    const dayCounts = new Map<string, number>();
    sessions.forEach((s) => {
      const dayKey = format(startOfDay(utcToLocal(s.createdAt)), 'yyyy-MM-dd');
      dayCounts.set(dayKey, (dayCounts.get(dayKey) || 0) + 1);
    });

    // Fill in every day from earliest session to today (no gaps)
    const sortedKeys = Array.from(dayCounts.keys()).sort();
    const start = startOfDay(new Date(sortedKeys[0]));
    const end = startOfDay(new Date());
    return eachDayOfInterval({ start, end }).map((day) => ({
      date: format(day, 'MMM d'),
      sessions: dayCounts.get(format(day, 'yyyy-MM-dd')) || 0,
    }));
  })();

  const sessionColumns = [
    {
      key: 'vehicle',
      header: 'Vehicle',
      render: (item: SessionSummary) => (
        <div>
          <p className="font-medium">{formatVehicle(item.vehicleYear, item.vehicleMake, item.vehicleModel)}</p>
          <p className="text-gray-400 text-xs">{formatVin(item.vin)}</p>
        </div>
      ),
    },
    {
      key: 'startTime',
      header: 'Start Time',
      render: (item: SessionSummary) => (
        <span className="text-sm">{format(utcToLocal(item.createdAt), 'MMM d, yyyy h:mm a')}</span>
      ),
    },
    {
      key: 'endTime',
      header: 'End Time',
      render: (item: SessionSummary) => (
        <span className="text-sm text-gray-400">
          {item.updatedAt ? format(utcToLocal(item.updatedAt), 'MMM d, yyyy h:mm a') : '-'}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (item: SessionSummary) => (
        <span className="text-sm font-medium text-purple-400">
          {formatDuration(item.createdAt, item.updatedAt)}
        </span>
      ),
    },
    {
      key: 'dtcCount',
      header: 'DTCs',
      render: (item: SessionSummary) => (
        <Badge variant={item.dtcCount > 0 ? 'error' : 'success'}>
          {item.dtcCount}
        </Badge>
      ),
    },
    {
      key: 'hasReport',
      header: 'Report',
      render: (item: SessionSummary) =>
        item.hasReport ? (
          <Badge variant="info">Saved</Badge>
        ) : (
          <span className="text-gray-500">-</span>
        ),
    },
  ];

  // An auth failure (expired/stale token) is NOT "no subscription". Never show a paying customer
  // the paywall because their token died; send them back through sign-in instead (#164).
  if (entitlementError) {
    return <SignInAgain error={entitlementError} />;
  }

  if (!entitlement?.hasPro && !entitlementLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-obdai-gradient bg-opacity-20 flex items-center justify-center">
            <svg className="w-10 h-10 text-obdai-gradient-start" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Premium Subscription Required</h2>
          <p className="text-gray-400 mb-6 max-w-md">
            Upgrade to OBDAI Premium to access the web dashboard, scan history, stored reports, and more.
          </p>
          <a
            href="https://obdai.app/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-obdai-gradient text-white font-medium rounded-lg hover:opacity-90 transition"
          >
            Get OBDAI
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">
          Welcome back{entitlement?.email ? `, ${entitlement.email}` : ''}
        </p>
      </div>

      <AriaBalanceCard />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Total Sessions"
          value={isLoading ? '-' : totalSessions}
          icon={<img src={dashboardIcon} alt="" className="w-6 h-6" />}
        />
        <StatCard
          label="Vehicles"
          value={isLoading ? '-' : totalVehicles}
          icon={<img src={carsIcon} alt="" className="w-6 h-6" />}
        />
        <StatCard
          label="Saved Reports"
          value={isLoading ? '-' : totalReports}
          icon={<img src={reportsIcon} alt="" className="w-6 h-6" />}
        />
      </div>

      {/* Sessions Per Day Chart */}
      {chartData.length > 0 && (
        <Card title="Sessions Per Day">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6d7cff" />
                    <stop offset="100%" stopColor="#e866dd" />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#33324e',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                  }}
                  formatter={(value: number) => [`${value} session${value !== 1 ? 's' : ''}`, 'Sessions']}
                />
                <Bar
                  dataKey="sessions"
                  fill="url(#barGradient)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Recent Sessions */}
      <Card
        title="Recent Sessions"
        action={
          <Link to="/sessions" className="text-sm text-obdai-gradient-start hover:text-obdai-gradient-end">
            View All
          </Link>
        }
      >
        <Table
          columns={sessionColumns}
          data={(sessionsData?.sessions || []).slice(0, 5)}
          keyExtractor={(item) => item.sessionKey}
          onRowClick={(item) => navigate(`/sessions/${item.sessionKey}`)}
          isLoading={sessionsLoading}
          emptyMessage="No scan sessions yet. Start scanning with the OBDAI app!"
        />
      </Card>

      {/* Subscription Info */}
      {entitlement?.expiresAt && (
        <Card title="Subscription">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{paidPlanLabel(entitlement.plan)}</p>
              <p className="text-gray-400 text-sm">
                Expires {format(utcToLocal(entitlement.expiresAt), 'MMMM d, yyyy')}
              </p>
            </div>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
              Active
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}
